/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2020 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import AudioManager from '../Audio/AudioManager';
import RecallManager from '../Game/RecallManager';
import RecoveryManager from '../Game/RecoveryManager';
import StateDependencyComponent from '../StateManager/StateDependencyComponent';
import StateEvent from '../StateManager/StateEvent';
import { StateID } from '../StateManager/StateID';
import StateManager from '../StateManager/StateManager';
import StateReference from '../StateManager/StateReference';
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Decorators from '../Utility/Decorators';
import ILoadingInfo from '../Utility/Loading/ILoadingInfo';
import LoadingInfo from '../Utility/Loading/LoadingInfo';
import PrefabLoader from '../Utility/Prefab/PrefabLoader';
import SmartPrefab from '../Utility/Prefab/SmartPrefab';
import UrlHelper, { UrlParameter } from '../Utility/UrlHelper';


/** @hidden */
const property: typeof cc._decorator.property = cc._decorator.property;

/** Abstract base class for handling dismissal of splash screen. */
@cc._decorator.ccclass
export default abstract class SplashScreenComponent extends StateDependencyComponent
{
    public static readonly MAX_ALPHA: number = 255;

    /** State on which to dismiss splash normally. */
    @property(StateReference)
    protected dismissOnStateRef: StateReference = new StateReference(StateID.WaitForPlay, true);

    /** State on which to dismiss in recall scenario. */
    @property(StateReference)
    protected recallDismissStateRef: StateReference = new StateReference(StateID.Request, true);

    /** Time in seconds to schedule dismiss of splash. */
    @property(cc.Float)
    protected dismissDelaySS: number = 0.5;

    /** AudioManager used to unmute after splash screen hidden. */
    @property(InjectableComponent)
    protected audioManagerRef: InjectableComponent<AudioManager> = new InjectableComponent();

    /** RecoveryManager used to check if recovering. */
    @property(InjectableComponent)
    protected recoveryManagerRef: InjectableComponent<RecoveryManager> = new InjectableComponent();

    /** RecallManager to check if recoverying to hideSplash on recallDismissStateRef */
    @property(InjectableComponent)
    protected recallManagerRef: InjectableComponent<RecallManager> = new InjectableComponent();

    @property(cc.Integer)
    @SmartPrefab.CustomProperty()
    protected recoveryShare: number = 100;

    @property(cc.Float)
    protected debugAlpha: number = SplashScreenComponent.MAX_ALPHA - 100;

    protected prefabLoaderRef: InjectableComponent<PrefabLoader> = new InjectableComponent();
    protected stateManagerRef: InjectableComponent<StateManager> = new InjectableComponent(ReferenceGroup.MainGame);

    /** Has hidden splash. */
    protected isHidden: boolean = false;
    protected loadingInfo: ILoadingInfo = new LoadingInfo(200);
    private _prefabLoadingInfo: ILoadingInfo = this.loadingInfo.AddLoadingGroup(100);
    protected recoveryLoadingInfo: ILoadingInfo = this.loadingInfo.AddLoadingGroup(100);

    public DontUnMute: boolean = false;

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this.recoveryManagerRef.SetContext(RecoveryManager);
        this.recallManagerRef.SetContext(RecallManager);
        this.audioManagerRef.SetContext(AudioManager);
        this.prefabLoaderRef.SetContext(PrefabLoader);
        this.stateManagerRef.SetContext(StateManager);

        this.recoveryLoadingInfo.Total = this.recoveryShare || 1;
    }

    /** @hidden */
    protected start(): void
    {
        super.start(this.dismissOnStateRef, this.recallDismissStateRef);

        this.recoveryManagerRef.WithValue(recoveryManager => recoveryManager.OnRecoveryComplete(this.Hide),
            () => CLog.warn('No RecoveryManager conigured.', LogTypes.Configuration, this));
    }

    protected updateLoadingProgress(): void
    {
        this.prefabLoaderRef.WithValue(loader => this._prefabLoadingInfo.SetProgress(loader.Progress));
        this.recoveryManagerRef.WithValue(r => this.recoveryLoadingInfo.SetProgress(r.Progress));
    }

    /** @hidden */
    protected firstUpdate(dt: number): void
    {
        super.firstUpdate(dt);
        this.audioManagerRef.WithValue(audioManager => audioManager.Mute());
    }

    /** @hidden */
    protected update(): void
    {
        if (this.isHidden) { return; }
        this.updateUI();
    }

    protected abstract updateUI(): void;

    /** @hidden */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        const isRecall: boolean = this.recallManagerRef.WithValue(mgr => mgr.Recalling, false);

        if (!isRecall && this.dismissOnStateRef.Value === event.State)
        {
            this.Hide();
        }

        if (isRecall && this.recallDismissStateRef.Value === event.State)
        {
            this.Hide();
        }

        this.completeState(event.State);
    }

    public AddLoadingGroup(amount: number): ILoadingInfo
    {
        return this.loadingInfo.AddLoadingGroup(amount);
    }

    /** Hides splash screen display. */
    @Decorators.BindThis()
    public Hide(overrideRecovery: boolean = false): void
    {
        if (!overrideRecovery && this.recoveryManagerRef.WithValue(manager => manager.Recovering)) { return; }
        if (this.isHidden) { return; }

        this.loadingInfo.LoadAll();
        this.setStartVolume();

        this.scheduleOnce(() =>
        {
            this.hideSplashScreen();
            this.dismissOnStateRef.Value.RemoveDependency(this);
            this.recallDismissStateRef.Value.RemoveDependency(this);
        }, this.dismissDelaySS);
    }

    protected abstract hideSplashScreen(): void;

    private setStartVolume(): void
    {
        if (this.DontUnMute) { return; }

        const volume: number = (new UrlHelper()).GetParameter<number>('volume', UrlParameter.Number);
        if (volume != null && volume <= 0) { return; }

        this.audioManagerRef.WithValue(audioManager =>
            {
                audioManager.UnMute();
                if (!volume) { return; }

                audioManager.SetVolume(volume);
            });
    }
}
